import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, Users, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  governance: 'Gouvernance', faculty_level: 'Faculte', department_level: 'Departement',
  academic_staff: 'Corps Academique', academic_admin: 'Admin Academique',
  administration: 'Administration', student_life: 'Vie Estudiantine',
  student_role: 'Role Etudiant', specialized: 'Specialise', logistics: 'Logistique', custom: 'Personnalise'
};

export const PositionsTab = ({ institutionId }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [formNameFr, setFormNameFr] = useState('');
  const [formNameEn, setFormNameEn] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formCategory, setFormCategory] = useState('custom');
  const [formDesc, setFormDesc] = useState('');
  const [formScope, setFormScope] = useState('self');

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/core/institutions/${institutionId}/positions`); setPositions(data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [institutionId]);

  const createPosition = async () => {
    try {
      const { data } = await axios.post(`${API}/core/institutions/${institutionId}/positions`, {
        name_fr: formNameFr, name_en: formNameEn, code: formCode,
        category: formCategory, description_fr: formDesc, default_scope: formScope
      });
      toast.success(`Poste "${formNameFr}" cree`);
      setPositions([...positions, data.position]);
      setShowCreate(false);
      setFormNameFr(''); setFormNameEn(''); setFormCode(''); setFormCategory('custom'); setFormDesc(''); setFormScope('self');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const filtered = positions.filter(p => {
    const ms = p.name_fr?.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase());
    const mc = filterCat === 'all' || p.category === filterCat;
    return ms && mc;
  });

  const categories = [...new Set(positions.map(p => p.category))].sort();

  if (loading) return <div className="flex items-center justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="positions-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><Input className="pl-9" placeholder="Rechercher un poste..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Categorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-position-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau Poste</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(p => (
          <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`position-card-${p.code}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{p.name_fr}</p><p className="text-xs text-gray-500 mt-0.5">{p.code}</p></div>
                {p.is_system ? <Badge variant="secondary" className="text-[10px] ml-2">Systeme</Badge> : <Badge className="text-[10px] ml-2 bg-blue-600">Personnalise</Badge>}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[p.category] || p.category}</Badge>
                {p.assigned_users > 0 && <Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-1" />{p.assigned_users}</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-3 text-center">{filtered.length} poste(s) sur {positions.length}</p>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-position-dialog">
          <DialogHeader><DialogTitle>Nouveau Poste / Fonction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom (FR) *</Label><Input value={formNameFr} onChange={e => setFormNameFr(e.target.value)} placeholder="Ex: Responsable Relations Exterieures" /></div>
            <div><Label>Nom (EN)</Label><Input value={formNameEn} onChange={e => setFormNameEn(e.target.value)} /></div>
            <div><Label>Code *</Label><Input value={formCode} onChange={e => setFormCode(e.target.value.toUpperCase().replace(/\s/g, '_'))} placeholder="Ex: EXT_RELATIONS" /></div>
            <div><Label>Categorie</Label>
              <Select value={formCategory} onValueChange={setFormCategory}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Perimetre</Label>
              <Select value={formScope} onValueChange={setFormScope}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Personnel</SelectItem><SelectItem value="class">Classe</SelectItem>
                  <SelectItem value="department">Departement</SelectItem><SelectItem value="faculty">Faculte</SelectItem>
                  <SelectItem value="institution">Institution</SelectItem>
                </SelectContent></Select></div>
            <div><Label>Description</Label><Textarea value={formDesc} onChange={e => setFormDesc(e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={createPosition} disabled={!formNameFr || !formCode}>Creer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
